import React from 'react';
import styled from 'styled-components';
import { File } from '@lgg/isomorphic/types/__generated__/graphql';
import { Icon } from 'src/components/general/icon';
import { AttachmentPreviewModal } from 'src/components/pages/conversations/components/general/attachment-preview-modal';
import { useVisible } from 'src/hooks/use-visible';
import { downloadFile } from 'src/utils/download-file';
import { ContactAttachmentFileOptions } from './contact-attachment-file-options';

const AttachmentLintItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;

  & div.list-item {
    display: flex;
    align-items: center;
    gap: 5px;
    flex: 1;
    min-width: 0;
  }

  & .list-item-title {
    cursor: pointer;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 12px;
    color: ${({ theme }) => theme.colors.gogo};
  }

  & .list-item-menu {
    flex-shrink: 0; /* Prevent the menu icon from shrinking */
    margin-left: 8px;
    padding: 2px 12px;
  }

  & .list-item-icon path {
    fill: ${({ theme }) => theme.colors.geyser};
  }

  & svg {
    height: 12px;
  }
`;

const getPreviewType = (mimeType: string) => {
  if (mimeType?.startsWith('image/')) {
    return 'IMAGE';
  }
  if (mimeType?.startsWith('video/')) {
    return 'VIDEO';
  }

  return null;
};

export const ContactAttachmentItem: React.FC<{
  id: string;
  attachment: File;
  onDeleted: () => void;
}> = ({ id, attachment, onDeleted }) => {
  const {
    visible: isPreviewVisible,
    show: showPreview,
    close: closePreview,
  } = useVisible();

  const previewType = getPreviewType(attachment.mimeType);

  return (
    <>
      <AttachmentLintItem key={id} data-lgg-id="contact-attachment-item">
        <div className="list-item">
          <Icon className="list-item-icon" type="attachedImage" />
          <span
            onClick={async () => {
              if (previewType) {
                showPreview();
              } else
                await downloadFile({
                  fileUrl: attachment.url,
                  fileName: attachment.filename,
                });
            }}
            className="list-item-title"
          >
            {attachment.filename}
          </span>
        </div>
        <ContactAttachmentFileOptions
          id={id}
          attachment={attachment}
          onDeleted={onDeleted}
        />
      </AttachmentLintItem>
      {previewType && (
        <AttachmentPreviewModal
          visible={isPreviewVisible}
          onClose={closePreview}
          type={previewType}
          url={attachment.url}
          name={attachment.filename}
          mimeType={attachment.mimeType}
          testId="conversations-attachment-preview"
        />
      )}
    </>
  );
};
